import { useCallback, useEffect, useState } from "react";
import { Archive, Check } from "lucide-react";
import { auth } from "../../firebase";
import ArchiveCycleModal, { ArchiveResult, ArchiveSummary } from "../../components/archiveCycleModal";
import ArchiveList from "../../components/archiveList";
import { cn } from "../../lib/utils";
import { SETTINGS_CARD, SettingsHeader, useSettings } from "./SettingsLayout";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

const OUTLINE_BUTTON =
  "inline-flex h-11 flex-none items-center justify-center gap-2 rounded-xl border border-melt-hair bg-white px-4 text-[15px] font-medium text-melt-text1 transition-colors hover:bg-melt-paper disabled:opacity-50 disabled:hover:bg-white";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export default function ArchivesSettings() {
  const { user } = useSettings();
  const [current, setCurrent] = useState<ArchiveSummary | null>(null);
  const [currentError, setCurrentError] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [lastArchive, setLastArchive] = useState<ArchiveResult | null>(null);

  // Counts for the tracker as it is now (the cycle that would be archived)
  const loadCurrent = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return;
    setCurrentError(false);
    try {
      const r = await fetch(`${API_BASE}/archives/preview`, {
        headers: { Authorization: `Bearer ${await u.getIdToken()}` },
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json?.error);
      setCurrent(json.summary);
    } catch {
      setCurrentError(true);
    }
  }, []);

  useEffect(() => {
    if (user) loadCurrent();
  }, [user, loadCurrent]);

  const count = current?.count ?? 0;

  return (
    <>
      <SettingsHeader
        title="Archives"
        description="Starting a new recruiting season? Archive your current applications to clear your tracker. Archived cycles are saved here so you can look back at them."
      />

      <div className={cn(SETTINGS_CARD, "flex flex-wrap items-center justify-between gap-4 px-6 py-5")}>
        <div>
          <p className="text-[15px] font-semibold text-melt-text1">Current cycle</p>
          <p className="mt-1 text-sm text-melt-text2">
            {current
              ? `${plural(count, "application")} · ${plural(current.reached.interview, "interview")} · ${plural(current.reached.offer, "offer")}`
              : currentError
              ? "Couldn't load your tracker right now."
              : "Loading…"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowArchiveModal(true)}
          disabled={!!current && count === 0}
          className={OUTLINE_BUTTON}
        >
          <Archive size={16} strokeWidth={1.75} />
          Archive current cycle
        </button>
      </div>

      {lastArchive && (
        <div className="mt-4 flex items-center gap-2 rounded-[10px] bg-[#EAF4EF] px-4 py-3 text-sm text-[#2F6B54]">
          <Check size={16} strokeWidth={1.75} />
          Archived {plural(lastArchive.summary.count, "application")} into “{lastArchive.name}”.
        </div>
      )}

      <h2 className="mb-3 mt-10 text-lg font-semibold tracking-[-.02em] text-melt-text1">Past cycles</h2>
      {user && <ArchiveList uid={user.uid} />}

      <ArchiveCycleModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onArchived={(result) => {
          setLastArchive(result);
          loadCurrent();
        }}
      />
    </>
  );
}
